import { useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  optimizePrompt,
  type OptimizePromptInput,
  type PromptOptimizeContext,
} from '@/lib/prompt-optimize';
import type { EmailStyle } from '@/lib/generate';

const MIN_OPTIMIZE_CHARS = 8;

export interface PromptTextareaOptimizeMeta {
  language?: string;
  style?: EmailStyle;
  scope?: 'whole' | 'element';
  scope_hint?: string;
}

export interface PromptTextareaProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  rows?: number;
  placeholder?: string;
  className?: string;
  optimizeContext: PromptOptimizeContext;
  optimizeMeta?: PromptTextareaOptimizeMeta;
  /** Landing demo uses the public optimize endpoint. */
  demo?: boolean;
  id?: string;
}

export function PromptTextarea({
  value,
  onChange,
  disabled = false,
  rows = 6,
  placeholder,
  className,
  optimizeContext,
  optimizeMeta,
  demo = false,
  id,
}: PromptTextareaProps) {
  const [optimizing, setOptimizing] = useState(false);
  const [optimizeError, setOptimizeError] = useState<string | null>(null);
  const [optimizedOnce, setOptimizedOnce] = useState(false);

  const canOptimize = value.trim().length >= MIN_OPTIMIZE_CHARS && !disabled && !optimizing;

  async function handleOptimize() {
    if (!canOptimize) return;
    setOptimizing(true);
    setOptimizeError(null);

    const input: OptimizePromptInput = {
      prompt: value.trim(),
      context: optimizeContext,
      ...optimizeMeta,
    };

    try {
      const result = await optimizePrompt(input, { demo });
      onChange(result.prompt);
      setOptimizedOnce(true);
    } catch (e) {
      setOptimizeError(e instanceof Error ? e.message : 'Could not optimize prompt');
    } finally {
      setOptimizing(false);
    }
  }

  return (
    <div className="space-y-2">
      <Textarea
        id={id}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          if (optimizedOnce) setOptimizedOnce(false);
        }}
        rows={rows}
        disabled={disabled || optimizing}
        placeholder={placeholder}
        className={cn('resize-none text-sm', className)}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleOptimize}
          disabled={!canOptimize}
          title={
            value.trim().length < MIN_OPTIMIZE_CHARS
              ? `Enter at least ${MIN_OPTIMIZE_CHARS} characters to optimize`
              : 'Expand your brief with AI before generating'
          }
          className="text-primary bg-accent/50 hover:bg-primary/10 border-primary/20 transition-colors"
        >
          {optimizing ? (
            <>
              <Loader2 className="size-3 animate-spin" />
              Optimizing…
            </>
          ) : (
            <>
              <Sparkles className="size-3" />
              Optimize prompt
            </>
          )}
        </Button>
        {optimizedOnce ? (
          <span className="text-success text-[11px]">Brief updated — review, then run AI</span>
        ) : (
          <span className="text-muted-foreground/80 text-[11px]">
            Sharpen your intent before generating
          </span>
        )}
      </div>
      {optimizeError ? (
        <p className="text-destructive text-[11px]" role="alert">
          {optimizeError}
        </p>
      ) : null}
    </div>
  );
}
